import { State } from '@meraki-internal/state';
import { ICareMap } from '../../caremap/ICareMap';
import { CareMapState } from '../../caremap/CareMapState';
import { WithLinks } from '../../caremap/WithLinks';
import { ICaremapNote, NotesClient } from './NotesClient';
import { ICareMapNoteTarget } from './ICareMapNoteTarget';
import { AlertPresenter } from '../../AlertBinder';
import { NoteReactionsModelFactory } from '../../community/reactions/ReactionsModel';
import { nanoid } from 'nanoid';
import { CareMapNoteTracker } from './CareMapNoteTracker';
import { ToastPresenter } from '../../ToastBinder';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { NewNoteFormViewModel } from '../add-note/NewNoteFormViewModel';
import { EditNoteFormViewModel } from '../edit-note/EditNoteFormViewModel';
import { FileUploader } from '../generic-notes/attachments/FileUploader';
import { NoteFormTotalAttachmentCountHelper } from './NoteFormTotalAttachmentCountHelper';
import { NoteValidator } from './NoteValidator';
import { BufferedEventBus } from '../../utils/BufferedEventBus';
import { IResourceChangedSocketMessage } from '../../web-sockets/SocketState';
import { Logger } from '../../support/Logger';
import { NotesModel } from './NotesModel';
import { NotePinsState } from '../pins/NotePinsState';

export class CaremapNotesModelFactory extends State<Record<string, never>> {
    static inject = () => [
        CareMapState,
        NotesClient,
        AlertPresenter,
        NoteReactionsModelFactory,
        CareMapNoteTracker,
        ToastPresenter,
        HistoryViewModel,
        FileUploader,
        NoteFormTotalAttachmentCountHelper,
        NoteValidator,
        BufferedEventBus,
        Logger,
        NotePinsState
    ];
    constructor(
        public careMapState: CareMapState,
        public notesClient: NotesClient,
        public alert: AlertPresenter,
        public reactionsFactory: NoteReactionsModelFactory,
        public tracking: CareMapNoteTracker,
        public toastPresenter: ToastPresenter,
        public history: HistoryViewModel,
        public fileUploader: FileUploader,
        public attachmentCountHelper: NoteFormTotalAttachmentCountHelper,
        public validator: NoteValidator,
        public events: BufferedEventBus,
        public logger: Logger,
        public notePinsState: NotePinsState
    ) {
        super({});
        this.attachmentCountHelper.factory = this;

        // events allows a limited number of subscribers
        // so subscribe once here and pass the message on
        this.events.on('web-sockets.message', msg => {
            for (const model of Object.values(this.careMapIdToModel)) {
                model.onWebsocketMessage(msg as IResourceChangedSocketMessage);
            }
        });

        this.events.on('PushNotificationListener.reload-required', async ({ resource, onReload }: {resource?: string, onReload?: () => void} = {}) => {
            if (!resource) {
                throw new Error('reload-required: missing resource');
            }
            const regex = /^\/users\/([^/]*)\/caremaps\/([^/]*)\/notes\/([^/]*)$/;
            const match = regex.exec(resource || '');
            if (match) {
                const careMapId = match[2];
                let model = this.careMapIdToModel[careMapId];
                if (!model) {
                    const caremap = await this.careMapState.getOrLoadCareMap(careMapId) as WithLinks<ICareMap>;
                    model = new NotesModel(caremap, this);
                    this.careMapIdToModel[careMapId!] = model;
                }
                await model.refresh(resource);
                if (onReload) {
                    onReload();
                }
            }
        });
    }

    private activeCareMapId = '';
    private careMapIdToModel: { [careMapId: string]: NotesModel } = {};

    forActiveCaremap = (): NotesModel => {
        const activeCaremap = this.careMapState.getActiveCaremap() as WithLinks<ICareMap>;

        if (!activeCaremap) {
            // TODO: consider instead returning the most recent one
            // to avoid transition errors
            throw new Error('there is no active caremap');
        }

        let model = this.careMapIdToModel[activeCaremap.careMapId!];
        if (!model) {
            model = new NotesModel(activeCaremap, this);
            this.careMapIdToModel[activeCaremap.careMapId!] = model;
        }

        if (this.activeCareMapId !== activeCaremap.careMapId) {
            this.activeCareMapId = activeCaremap.careMapId!;

            model.load().catch(err => {
                this.alert.showAndLogError(err);
            });

            // Load pins for the new caremap (skip if no notePins link, e.g. anonymous users)
            if (activeCaremap.links.notePins) {
                this.notePinsState.load(activeCaremap.links.notePins).catch(err => {
                    if (err.errorCode === 'fetch.failed') {
                        return;
                    }
                    this.logger.error(`Failed to load note pins: ${err.toString()}`);
                });
            }

            // forActiveCaremap is likely called in a render
            // so we need to avoid doing anything sync that might change state
            // or react gets upset
            setTimeout(() => {
                // if the model changes, we change, that way components can just subscrive to the factory
                model.subscribe(() => {
                    this.setState({});
                });
                // if pins change, we change, so components re-render with updated pin state
                this.notePinsState.subscribe(() => {
                    this.setState({});
                });
            });
        }

        return this.careMapIdToModel[activeCaremap.careMapId!];
    };

    createFormForNewNote = (noteTarget: ICareMapNoteTarget = { section: 'patient' }, note: Partial<ICaremapNote> = {}): NewNoteFormViewModel => {
        const newNote: ICaremapNote = {
            noteId: nanoid(),
            title: '',
            html: '',
            isPrivate: true,
            attachments: [],
            ...note,
            otherAttributes: {
                noteTarget
            }
        };
        return new NewNoteFormViewModel(newNote, this);
    };

    createEditForm = async (noteId: string): Promise<EditNoteFormViewModel> => {

        while (!this.forActiveCaremap().hasLoaded) {
            // TODO: timeout
            await new Promise(resolve => setTimeout(resolve, 250));
        }

        const note = this.forActiveCaremap().getNote(noteId);
        if (!note) {
            throw new Error(`note with id ${noteId} not found`);
        }

        return new EditNoteFormViewModel(note, this);
    };
};
